// OPFS 文件系统层：媒体文件（上传的原视频 + 预处理产物）的唯一读写入口。
//
// 路径规范：绝对虚拟路径，以 "/" 开头，用 "/" 分隔。目录布局：
//   /projects/<project-uuid>/media/<fileId>/<原名>.mp4   # 上传的原始媒体
//   /projects/<project-uuid>/.cache/<md5>/audio.m4a      # 预处理产物（音频）
//   /projects/<project-uuid>/.cache/<md5>/5fps_400.mp4   # 预处理产物（胶片条）
//
// 只存「文件」，不存「结构化数据」——结构化数据（protocol / 配置 / checkpoint）
// 全部在 IndexedDB（见 db.js）。两者靠 OPFS 虚拟路径字符串关联。

const rootPromise = navigator.storage.getDirectory();

function normalize(path) {
    if (typeof path !== 'string' || !path.startsWith('/')) {
        throw new Error(`OPFS path must be absolute and start with "/": ${path}`);
    }
    return path.split('/').filter(Boolean);
}

/** 逐级解析目录；create=false 且目录不存在时返回 null。 */
async function getDir(path, create = true) {
    let dir = await rootPromise;
    for (const part of normalize(path)) {
        if (create) {
            dir = await dir.getDirectoryHandle(part, { create: true });
        } else {
            try {
                dir = await dir.getDirectoryHandle(part);
            } catch (error) {
                if (error && error.name === 'NotFoundError') return null;
                throw error;
            }
        }
    }
    return dir;
}

function toBlob(data) {
    if (data instanceof Blob) return data;
    if (typeof data === 'string') return new Blob([data]);
    if (data instanceof ArrayBuffer) return new Blob([data]);
    // Uint8Array 直接 write 在部分浏览器里会落成 0 字节。先拷成 Blob。
    if (ArrayBuffer.isView(data)) return new Blob([data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength)]);
    return new Blob([data]);
}

/** 写入文件。data 接受 File / Blob / ArrayBuffer / TypedArray / string。 */
export async function putFile(path, data) {
    const parts = normalize(path);
    const name = parts.pop();
    const dir = await getDir('/' + parts.join('/'), true);
    const handle = await dir.getFileHandle(name, { create: true });
    const payload = toBlob(data);
    const writable = await handle.createWritable();
    try {
        await writable.write(payload);
        await writable.close();
    } catch (error) {
        try { await writable.abort(); } catch { /* noop */ }
        throw error;
    }
    const stored = await handle.getFile();
    if (payload.size > 0 && stored.size === 0) throw new Error(`写入后文件为空：${path}`);
    return handle;
}

/** 读取文件为 File（惰性，不整块进内存）。不存在返回 null。 */
export async function getFile(path) {
    const parts = normalize(path);
    const name = parts.pop();
    const dir = await getDir('/' + parts.join('/'), false);
    if (!dir) return null;
    try {
        const handle = await dir.getFileHandle(name);
        return await handle.getFile();
    } catch (error) {
        if (error && error.name === 'NotFoundError') return null;
        throw error;
    }
}

/** 生成 blob URL，供渲染 worker 直接 fetch（惰性引用，Range 读取不整块载入）。 */
export async function getFileUrl(path) {
    const file = await getFile(path);
    if (!file) throw new Error(`OPFS file not found: ${path}`);
    return URL.createObjectURL(file);
}

/** 删除文件或目录（递归）。不存在返回 false。 */
export async function remove(path) {
    const parts = normalize(path);
    const name = parts.pop();
    const dir = await getDir('/' + parts.join('/'), false);
    if (!dir) return false;
    try {
        await dir.removeEntry(name, { recursive: true });
        return true;
    } catch (error) {
        if (error && error.name === 'NotFoundError') return false;
        throw error;
    }
}

/** 列出目录下的条目。目录不存在返回空数组。 */
export async function list(dirPath) {
    const dir = await getDir(dirPath, false);
    if (!dir) return [];
    const result = [];
    for await (const [name, handle] of dir.entries()) {
        result.push({ name, kind: handle.kind }); // 'file' | 'directory'
    }
    return result;
}

/** 申请持久化存储（OPFS + IndexedDB 共用 origin quota）。 */
export async function persist() {
    if (!navigator.storage.persist) return false;
    return navigator.storage.persist();
}

/** 查询当前存储用量（used/quota）。 */
export async function estimate() {
    return navigator.storage.estimate();
}

export default { putFile, getFile, getFileUrl, remove, list, persist, estimate };
